// ============================================================
// COVER MODEL - how much of THEM does it hide?
//
// Cover is not a property of a wall. The same 1.6 m wall is full cover
// to a gaunt, three-quarter cover to a guardsman, half to a Space Marine
// and nothing at all to a Warhound. So cover cannot be baked into the
// terrain: it is worked out when a shot is measured, from the obstacle's
// height and the TARGET'S OWN.
//
// The thresholds are not invented. `COVER_LEVELS` in engine/constants.ts
// already states which body parts each level protects:
//
//     low           legs
//     half          legs + body
//     threeQuarter  legs + body + arms
//     full          everything
//
// which is exactly the bottom 25%, 50%, 75% and 100% of a body. So "the
// wall hides half of them" and "half cover protects body and legs" stop
// being two systems that happen to agree and become one statement.
//
// Pure. Zero React dependency.
// ============================================================
import type { Cover } from '../../types';
import { SIZE_HEIGHTS } from '../constants';

/** A human, for anything that arrives without a size. */
export const DEFAULT_TARGET_HEIGHT = SIZE_HEIGHTS.Average;

/** Eyes sit a little below the top of the head, whatever the creature. */
const EYE_RATIO = 0.9;

/**
 * The fraction of a body each cover level hides.
 *
 * Read straight off COVER_LEVELS - see the header. Ordered high to low so
 * the first match wins.
 */
export const COVER_FRACTIONS: Record<Exclude<Cover['level'], 'none'>, number> = {
  full: 1,
  threeQuarter: 0.75,
  half: 0.5,
  low: 0.25,
};

const BY_DESCENDING_FRACTION = (Object.keys(COVER_FRACTIONS) as Exclude<Cover['level'], 'none'>[]).sort(
  (a, b) => COVER_FRACTIONS[b] - COVER_FRACTIONS[a],
);

/** How tall this size category stands, in metres. */
export function heightForSize(size: string | undefined | null): number {
  return (size && SIZE_HEIGHTS[size]) || DEFAULT_TARGET_HEIGHT;
}

/** How far off their own feet this size category's eyes are. */
export function eyeHeightForSize(size: string | undefined | null): number {
  return heightForSize(size) * EYE_RATIO;
}

/**
 * The cover an obstacle grants, from how much of the target it hides.
 *
 * @param hidden        metres of the target obscured - the obstacle's top
 *                      measured from the TARGET'S FEET, so standing on a
 *                      crate or down in a trench is already accounted for
 * @param targetHeight  the target's own height in metres
 */
export function deriveCoverLevel(hidden: number, targetHeight: number): Cover['level'] {
  if (!(targetHeight > 0) || !(hidden > 0)) return 'none';
  const ratio = hidden / targetHeight;
  for (const level of BY_DESCENDING_FRACTION) {
    if (ratio >= COVER_FRACTIONS[level]) return level;
  }
  return 'none';
}
